import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Download, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';

const Hero = () => {
  const [displayText, setDisplayText] = useState('');
  const titles = ['Full Stack Developer', 'Python Developer', 'React Developer', 'Problem Solver'];
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentTitle = titles[currentTitleIndex];
    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          if (displayText.length < currentTitle.length) {
            setDisplayText(currentTitle.slice(0, displayText.length + 1));
          } else {
            setTimeout(() => setIsDeleting(true), 2000);
          }
        } else {
          if (displayText.length > 0) {
            setDisplayText(displayText.slice(0, -1));
          } else {
            setIsDeleting(false);
            setCurrentTitleIndex((currentTitleIndex + 1) % titles.length);
          }
        }
      },
      isDeleting ? 50 : 100
    );

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentTitleIndex]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownloadResume = () => {
    window.open('https://customer-assets.emergentagent.com/job_64cecb61-a4c5-415c-ab00-968493445439/artifacts/ian00mbh_Aditya-Yadav-FullStack.pdf', '_blank');
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10b981]/5 via-transparent to-[#06b6d4]/5"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.03) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="text-[#06b6d4] font-mono text-sm">Hi, my name is</p>
                <h1 className="text-5xl md:text-6xl font-bold text-white">
                  {personalInfo.name}
                </h1>
                <div className="h-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#a3a3a3]">
                    <span className="text-[#10b981]">{displayText}</span>
                    <span className="animate-pulse">|</span>
                  </h2>
                </div>
              </div>

              <p className="text-[#a3a3a3] text-lg leading-relaxed">
                {personalInfo.bio.substring(0, 200)}...
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => scrollToSection('projects')}
                  className="bg-[#10b981] hover:bg-[#059669] text-white px-8"
                >
                  View My Work
                </Button>
                <Button
                  onClick={handleDownloadResume}
                  variant="outline"
                  className="border-[#06b6d4] text-[#06b6d4] hover:bg-[#06b6d4] hover:text-white px-8"
                >
                  <Download className="mr-2" size={18} />
                  Resume
                </Button>
              </div>

              {/* Social Links */}
              <div className="flex gap-4 pt-4">
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-lg bg-[#121214] border border-[#1f1f23] flex items-center justify-center text-[#a3a3a3] hover:text-[#10b981] hover:border-[#10b981] transition-all duration-300 hover:scale-110"
                >
                  <Github size={20} />
                </a>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-lg bg-[#121214] border border-[#1f1f23] flex items-center justify-center text-[#a3a3a3] hover:text-[#06b6d4] hover:border-[#06b6d4] transition-all duration-300 hover:scale-110"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="w-12 h-12 rounded-lg bg-[#121214] border border-[#1f1f23] flex items-center justify-center text-[#a3a3a3] hover:text-[#f97316] hover:border-[#f97316] transition-all duration-300 hover:scale-110"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#10b981] via-[#06b6d4] to-[#f97316] rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
                <div className="relative">
                  <img
                    src={personalInfo.image}
                    alt={personalInfo.name}
                    className="w-80 h-80 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 rounded-lg border-2 border-[#10b981] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={() => scrollToSection('about')}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-[#a3a3a3] hover:text-[#10b981] transition-colors duration-300 animate-bounce"
      >
        <ChevronDown size={32} />
      </button>
    </section>
  );
};

export default Hero;
